import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
var _excluded = ["alt", "fallbackImg", "src", "draggable"];
import PropTypes from 'prop-types';
import React, { forwardRef, useContext } from 'react';
import { DRAGGABLE_VALUES, PHOTO_ORIENTATIONS } from '../../constants';
import { Image } from '../Image/Image';
import { PropertyCardContext } from '../PropertyCard/PropertyCard.context';
import { PropertyCardPhotoFallback } from '../PropertyCardPhotoFallback/PropertyCardPhotoFallback';
import { StyledPropertyCardPhoto } from './PropertyCardPhoto.styles';

/**
 * Used to display a single property photo in a `PropertyCard`.
 *
 * @since 8.63.0
 */
export var PropertyCardPhoto = /*#__PURE__*/forwardRef(function (_ref, forwardedRef) {
  var alt = _ref.alt,
    fallbackImg = _ref.fallbackImg,
    src = _ref.src,
    draggable = _ref.draggable,
    rest = _objectWithoutProperties(_ref, _excluded);
  var context = useContext(PropertyCardContext);
  var ImageComponent = src ? /*#__PURE__*/React.createElement(Image, {
    src: src,
    alt: alt,
    "aria-hidden": "false",
    draggable: draggable
  }) : fallbackImg;
  return /*#__PURE__*/React.createElement(StyledPropertyCardPhoto, _extends({
    appearance: context.appearance,
    photoSrc: src,
    ref: forwardedRef
  }, rest), ImageComponent);
});
PropertyCardPhoto.displayName = 'PropertyCard.Photo';
PropertyCardPhoto.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * The image to display if no photo is available.
   *
   * Takes a component that is displayed if no `src` value is provided.
   */
  fallbackImg: PropTypes.node,
  /**
   * Determines how to display the photo. Vertically-oriented photos get special
   * treatment to ensure they fill their container.
   *
   * If the photo is square, use ‘portrait’.
   */
  photoOrientation: PropTypes.oneOf(Object.values(PHOTO_ORIENTATIONS)),
  /**
   * The URL of the property photo, Google Street View image or satellite image.
   * If none is provided, the `fallbackImg` is displayed instead.
   */
  src: PropTypes.string,
  /**
   * Indicates whether the image can be dragged, either with native browser behavior
   * or the HTML Drag and Drop API.
   *
   * If no value is provided, 'draggable' defaults to 'auto'
   */
  draggable: PropTypes.oneOf(Object.values(DRAGGABLE_VALUES))
} : {};
PropertyCardPhoto.defaultProps = {
  fallbackImg: /*#__PURE__*/React.createElement(PropertyCardPhotoFallback, null),
  photoOrientation: PHOTO_ORIENTATIONS.LANDSCAPE,
  src: '',
  draggable: DRAGGABLE_VALUES.AUTO
};